import type { TimelineEvent } from "@/api/observability";
import { cn } from "@/lib/utils";

export type TimelineSummaryEntry = {
  id?: string;
  timestamp?: string | null;
  relative?: string | null;
  agent?: string | null;
  thread?: string | null;
  eventType?: string | null;
  summary?: string | null;
};

type TimelineSummaryProps = {
  entries: TimelineSummaryEntry[];
  className?: string;
  emptyLabel?: string;
  onThreadClick?: (agent: string, thread: string) => void;
};

function formatTimestamp(timestamp?: string | null, relative?: string | null) {
  if (!timestamp) return relative ?? "";
  const date = new Date(timestamp);
  const time = Number.isNaN(date.getTime()) ? timestamp : date.toLocaleString();
  return relative ? `${time} (${relative})` : time;
}

export function timelineEventToSummary(event: TimelineEvent, index = 0): TimelineSummaryEntry {
  return {
    id: `${event.timestamp}-${index}`,
    timestamp: event.timestamp,
    relative: event.relative,
    agent: event.agent,
    thread: event.thread,
    eventType: event.event_type,
    summary: event.summary ?? event.note?.summary ?? "",
  };
}

export function TimelineSummary({ entries, className, emptyLabel = "No events recorded.", onThreadClick }: TimelineSummaryProps) {
  if (!entries.length) {
    return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
  }

  return (
    <div className={cn("space-y-4 max-h-[70vh] overflow-y-auto", className)}>
      {entries.map((entry, index) => (
        <article
          key={entry.id ?? `${entry.timestamp}-${index}`}
          className="border border-border rounded-xl p-4 bg-background/40 hover:border-amber-400 transition"
        >
          <div className="mb-2 flex flex-wrap items-center justify-between text-xs text-muted-foreground">
            <div className="flex flex-wrap items-center gap-2">
              {entry.agent && (
                <span className="px-2 py-0.5 rounded-full bg-muted text-primary-foreground dark:text-amber-200 font-semibold">{entry.agent}</span>
              )}
              {entry.thread && entry.agent && (
                <button
                  type="button"
                  className="px-2 py-0.5 rounded-full bg-muted text-blue-200 lowercase"
                  onClick={() => onThreadClick?.(entry.agent!, entry.thread!)}
                >
                  {entry.thread}
                </button>
              )}
            </div>
            <span>{formatTimestamp(entry.timestamp, entry.relative)}</span>
          </div>
          {entry.eventType && <div className="mb-1 text-xs uppercase text-muted-foreground">{entry.eventType}</div>}
          <p className="text-sm text-foreground/80 whitespace-pre-wrap break-words">{entry.summary || "—"}</p>
        </article>
      ))}
    </div>
  );
}

export default TimelineSummary;
